import { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  User,
  Server,
  Copy,
  Check,
  Bold,
  Italic,
  Underline,
  Link2,
  Image as ImageIcon,
  Eraser,
  Bell,
  Volume2,
  VolumeX,
  Smartphone,
  Palette,
  Bot,
  Key,
  RefreshCw,
} from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import { useNotificationStore } from "../../stores/useNotificationStore";
import { useThemeStore, ACCENT_PALETTE } from "../../stores/themeStore";
import { useApi } from "../../hooks/useApi";
import {
  getApiBaseUrl,
  getCustomApiBaseUrl,
  setCustomApiBaseUrl,
  DEFAULT_API_URL,
} from "../../utils/apiConfig";
import { sanitizeHtml } from "../../utils/sanitize";
import { alertDialog } from "../../stores/dialogStore";
import EmailSpinner from "./EmailSpinner";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function getMailConfig(userEmail?: string) {
  let domain = "";
  if (userEmail && userEmail.includes("@")) {
    domain = userEmail.split("@")[1].toLowerCase();
  } else {
    const host = window.location.hostname.replace(/^webmail\./, "");
    domain = host.toLowerCase();
  }
  const mailHost = `mail.${domain}`;
  return { domain, mailHost };
}

export default function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const { get, patch } = useApi();
  const {
    notificationsEnabled,
    soundEnabled,
    permission,
    enableNotifications,
    setSoundEnabled,
    testNotification,
    checkPermission,
    installPrompt,
  } = useNotificationStore();

  const isDark = useThemeStore((s) => s.isDark);
  const accentColor = useThemeStore((s) => s.accentColor);
  const setAccentColor = useThemeStore((s) => s.setAccentColor);

  const [activeTab, setActiveTab] = useState<"general" | "appearance" | "notifications" | "imap-smtp" | "mcp">("general");
  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [signature, setSignature] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedError, setSavedError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [mcpData, setMcpData] = useState<{
    mcpUrl?: string;
    mcpToken?: string;
    mcpUrlWithToken?: string;
  } | null>(null);
  const [loadingMcp, setLoadingMcp] = useState(false);
  const [serverUrlInput, setServerUrlInput] = useState(getCustomApiBaseUrl() || DEFAULT_API_URL);
  const [serverSaved, setServerSaved] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  const signatureRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mailHost } = getMailConfig(user?.email);

  const handleSaveCustomServer = () => {
    if (!serverUrlInput.trim()) {
      setCustomApiBaseUrl(null);
    } else {
      setCustomApiBaseUrl(serverUrlInput.trim());
    }
    setServerSaved(true);
    setTimeout(() => setServerSaved(false), 2000);
  };

  const handleResetServer = () => {
    setCustomApiBaseUrl(null);
    setServerUrlInput(DEFAULT_API_URL);
    setServerSaved(true);
    setTimeout(() => setServerSaved(false), 2000);
  };

  const fetchMcpToken = useCallback(async () => {
    setLoadingMcp(true);
    try {
      const data = await get<{
        mcpUrl: string;
        mcpToken: string;
        mcpUrlWithToken: string;
        expiresAt: number;
      }>("/mail/settings/mcp-token");
      setMcpData(data);
    } catch {
      // Endpoint error
    } finally {
      setLoadingMcp(false);
    }
  }, [get]);

  const imapConfig = [
    { label: "Servidor IMAP", value: mailHost, id: "imap-host" },
    { label: "Puerto IMAP", value: "993", id: "imap-port" },
    { label: "Cifrado IMAP", value: "SSL / TLS", id: "imap-ssl" },
    { label: "Servidor SMTP", value: mailHost, id: "smtp-host" },
    { label: "Puerto SMTP", value: "587 (o 465)", id: "smtp-port" },
    { label: "Cifrado SMTP", value: "STARTTLS (o SSL/TLS)", id: "smtp-ssl" },
    { label: "Usuario", value: user?.email || "", id: "smtp-user" },
    { label: "Autenticación", value: "Contraseña normal", id: "smtp-auth" },
  ];

  useEffect(() => {
    if (!isOpen) return;
    checkPermission();
    const loadSettings = async () => {
      try {
        const data = await get<{
          displayName?: string;
          signature?: string;
        }>("/mail/settings");
        if (data.displayName) setDisplayName(data.displayName);
        if (data.signature && signatureRef.current) {
          signatureRef.current.innerHTML = sanitizeHtml(data.signature).html;
          setSignature(data.signature);
        }
      } catch {
        // Settings endpoint may not exist yet
      }
    };
    loadSettings();
  }, [isOpen, get, checkPermission]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const execCmd = useCallback((cmd: string, val?: string) => {
    signatureRef.current?.focus();
    document.execCommand(cmd, false, val);
  }, []);

  const handleSignatureInput = () => {
    if (signatureRef.current) {
      setSignature(signatureRef.current.innerHTML);
    }
  };

  const handleInsertLink = useCallback(() => {
    const url = prompt("URL del enlace:");
    if (url) execCmd("createLink", url);
  }, [execCmd]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alertDialog("Por favor selecciona un archivo de imagen válido (PNG, JPG, SVG, WebP)", "Formato no válido");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alertDialog("La imagen es demasiado grande. El tamaño máximo permitido es 2 MB.", "Tamaño excedido");
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${getApiBaseUrl()}/mail/upload-image`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Error al subir imagen");
      }
      const data = await res.json();
      const imgHtml = `<img src="${data.url}" alt="logo" class="email-signature-img" />`;
      execCmd("insertHTML", imgHtml);
      handleSignatureInput();
    } catch (err: any) {
      alertDialog(err?.message || "Error al subir imagen", "Error al subir");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaved(false);
    setSavedError(null);
    try {
      const finalSignature = signatureRef.current?.innerHTML || signature;
      await patch("/mail/settings", {
        displayName: displayName.trim(),
        signature: finalSignature,
      });
      setSignature(finalSignature);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      setSavedError(err?.message || "Error al guardar");
      setTimeout(() => setSavedError(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleInstallApp = async () => {
    if (!installPrompt) {
      alertDialog(
        "Para instalar MagicMail en iOS/Safari:\n1. Presiona el botón de 'Compartir' (cuadro con flecha).\n2. Selecciona 'Agregar a pantalla de inicio'.",
        "Instalación en iOS / Safari"
      );
      return;
    }
    installPrompt.prompt();
    const choiceResult = await installPrompt.userChoice;
    if (choiceResult.outcome === "accepted") {
      useNotificationStore.getState().setInstallPrompt(null);
    }
  };



  if (!isOpen) return null;

  return (
    <div
      ref={backdropRef}
      className="dialog-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      <div className="dialog-panel w-[580px] max-w-[94vw] max-h-[85vh] flex flex-col bg-[var(--bg-primary)] border border-[var(--border-subtle)] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
          <h2 className="text-xs font-bold uppercase tracking-wider text-[var(--text-primary)]">
            Configuración
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X size={15} />
          </button>
        </div>

        {/* Tabs - Scrollable horizontalmente en pantallas pequeñas */}
        <div className="flex border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)] px-3 text-xs font-medium gap-1 overflow-x-auto no-scrollbar touch-pan-x flex-nowrap w-full">
          <button
            onClick={() => setActiveTab("general")}
            className={`flex items-center gap-1.5 px-3 py-2 transition-colors border-b-2 flex-shrink-0 whitespace-nowrap ${
              activeTab === "general"
                ? "border-accent text-accent font-semibold"
                : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
          >
            <User size={13} />
            General
          </button>
          <button
            onClick={() => setActiveTab("appearance")}
            className={`flex items-center gap-1.5 px-3 py-2 transition-colors border-b-2 flex-shrink-0 whitespace-nowrap ${
              activeTab === "appearance"
                ? "border-accent text-accent font-semibold"
                : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
          >
            <Palette size={13} />
            Apariencia
          </button>
          <button
            onClick={() => setActiveTab("notifications")}
            className={`flex items-center gap-1.5 px-3 py-2 transition-colors border-b-2 flex-shrink-0 whitespace-nowrap ${
              activeTab === "notifications"
                ? "border-accent text-accent font-semibold"
                : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
          >
            <Bell size={13} />
            Notificaciones
          </button>
          <button
            onClick={() => setActiveTab("imap-smtp")}
            className={`flex items-center gap-1.5 px-3 py-2 transition-colors border-b-2 flex-shrink-0 whitespace-nowrap ${
              activeTab === "imap-smtp"
                ? "border-accent text-accent font-semibold"
                : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
          >
            <Server size={13} />
            Servidores
          </button>
          <button
            onClick={() => {
              setActiveTab("mcp");
              if (!mcpData && !loadingMcp) {
                fetchMcpToken();
              }
            }}
            className={`flex items-center gap-1.5 px-3 py-2 transition-colors border-b-2 flex-shrink-0 whitespace-nowrap ${
              activeTab === "mcp"
                ? "border-accent text-accent font-semibold"
                : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
          >
            <Bot size={13} />
            Conexión IA (MCP)
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === "general" && (
            <div className="space-y-4">
              {/* Display name */}
              <div>
                <label className="block text-2xs font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Tu nombre"
                  className="w-full px-3 py-1.5 text-xs bg-[var(--bg-secondary)] border-0 text-[var(--text-primary)] outline-none focus:ring-1 focus:ring-accent transition-colors"
                />
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-2xs font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                  Correo electrónico
                </label>
                <input
                  type="text"
                  value={user?.email || ""}
                  readOnly
                  className="w-full px-3 py-1.5 text-xs bg-[var(--bg-secondary)] border-0 text-[var(--text-muted)] cursor-not-allowed outline-none opacity-70"
                />
              </div>

              {/* Signature */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-2xs font-bold uppercase tracking-wider text-[var(--text-muted)]">
                    Firma
                  </label>
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => execCmd("bold")}
                      className="p-1 hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                      title="Negrita"
                    >
                      <Bold size={12} />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => execCmd("italic")}
                      className="p-1 hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                      title="Cursiva"
                    >
                      <Italic size={12} />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => execCmd("underline")}
                      className="p-1 hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                      title="Subrayado"
                    >
                      <Underline size={12} />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleInsertLink}
                      className="p-1 hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                      title="Insertar enlace"
                    >
                      <Link2 size={12} />
                    </button>
                    <label
                      className="p-1 hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors cursor-pointer inline-flex items-center justify-center"
                      title="Insertar imagen"
                    >
                      {uploadingImage ? <EmailSpinner size={12} /> : <ImageIcon size={12} />}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        className="hidden"
                      />
                    </label>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => execCmd("removeFormat")}
                      className="p-1 hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                      title="Limpiar formato"
                    >
                      <Eraser size={12} />
                    </button>
                  </div>
                </div>
                <div
                  ref={signatureRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleSignatureInput}
                  className="w-full px-3 py-2 text-xs bg-[var(--bg-secondary)] border-0 text-[var(--text-primary)] outline-none focus:ring-1 focus:ring-accent transition-colors min-h-[120px] max-h-[200px] overflow-y-auto whitespace-pre-wrap font-sans"
                />
              </div>
            </div>
          )}

          {activeTab === "appearance" && (
            <div className="space-y-5">
              {/* Modo Claro / Oscuro */}
              <div>
                <label className="block text-2xs font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                  Modo de visualización
                </label>
                <p className="text-2xs text-[var(--text-muted)] mb-3">
                  Elige entre el tema claro con alto contraste o el tema oscuro optimizado para ambientes de poca luz.
                </p>
                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => useThemeStore.getState().setTheme(false)}
                    className={`flex items-center gap-2.5 p-3 bg-[var(--bg-secondary)] text-left transition-all border ${
                      !isDark
                        ? "border-[var(--text-primary)] ring-1 ring-[var(--text-primary)]"
                        : "border-transparent hover:bg-[var(--bg-tertiary)]"
                    }`}
                  >
                    <div className="w-4 h-4 rounded-none bg-white border border-gray-300 flex items-center justify-center flex-shrink-0">
                      {!isDark && <Check size={10} className="text-black" strokeWidth={3} />}
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-[var(--text-primary)] block">Modo Claro</span>
                      <span className="text-[10px] text-[var(--text-muted)] block">Fondo limpio y nítido</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => useThemeStore.getState().setTheme(true)}
                    className={`flex items-center gap-2.5 p-3 bg-[var(--bg-secondary)] text-left transition-all border ${
                      isDark
                        ? "border-[var(--text-primary)] ring-1 ring-[var(--text-primary)]"
                        : "border-transparent hover:bg-[var(--bg-tertiary)]"
                    }`}
                  >
                    <div className="w-4 h-4 rounded-none bg-neutral-900 border border-neutral-700 flex items-center justify-center flex-shrink-0">
                      {isDark && <Check size={10} className="text-white" strokeWidth={3} />}
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-[var(--text-primary)] block">Modo Oscuro</span>
                      <span className="text-[10px] text-[var(--text-muted)] block">Fondo carbón / descanso visual</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Color de Acento */}
              <div>
                <label className="block text-2xs font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                  Color de resalte e identidad
                </label>
                <p className="text-2xs text-[var(--text-muted)] mb-3">
                  Personaliza los botones principales, bordes activos, indicadores de no leídos y selecciones con tu color favorito.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {ACCENT_PALETTE.map((item) => {
                    const isSelected = accentColor === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setAccentColor(item.id)}
                        className={`flex items-center gap-3 p-2.5 bg-[var(--bg-secondary)] text-left transition-all border ${
                          isSelected
                            ? "border-[var(--text-primary)] ring-1 ring-[var(--text-primary)]"
                            : "border-transparent hover:bg-[var(--bg-tertiary)]"
                        }`}
                      >
                        <span
                          className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-white text-xs shadow-sm"
                          style={{ backgroundColor: item.accent }}
                        >
                          {isSelected && <Check size={12} strokeWidth={3} />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <span className="text-xs font-semibold text-[var(--text-primary)] block truncate">
                            {item.name}
                          </span>
                          <span className="text-[10px] font-mono text-[var(--text-muted)] block uppercase">
                            {item.accent}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Vista previa en vivo */}
              <div className="p-3 bg-[var(--bg-secondary)] border border-[var(--border-subtle)] space-y-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)] block">
                  Vista previa de elementos con este color
                </span>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    type="button"
                    className="px-3 py-1 text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors shadow-sm"
                  >
                    Botón Principal
                  </button>
                  <span className="px-2 py-0.5 text-2xs font-semibold bg-accent-light text-accent border border-accent">
                    Etiqueta / Tag
                  </span>
                  <span className="text-xs font-semibold text-accent underline">
                    Enlace de ejemplo
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === "notifications" && (
            <div className="space-y-3">
              {/* Push Notifications Card */}
              <div className="p-3.5 bg-[var(--bg-secondary)] flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Bell size={16} className="text-accent flex-shrink-0" />
                  <div>
                    <h4 className="text-xs font-semibold text-[var(--text-primary)]">
                      Notificaciones en pantalla
                    </h4>
                    <p className="text-2xs text-[var(--text-muted)]">
                      Alertas de nuevos correos entrantes
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {permission !== "granted" ? (
                    <button
                      onClick={enableNotifications}
                      className="px-3 py-1 text-2xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors"
                    >
                      Activar
                    </button>
                  ) : (
                    <button
                      onClick={testNotification}
                      className="px-2.5 py-1 text-2xs font-medium bg-[var(--bg-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                    >
                      Probar
                    </button>
                  )}
                </div>
              </div>

              {/* Sound Chime Card */}
              <div className="p-3.5 bg-[var(--bg-secondary)] flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  {soundEnabled ? (
                    <Volume2 size={16} className="text-accent flex-shrink-0" />
                  ) : (
                    <VolumeX size={16} className="text-[var(--text-muted)] flex-shrink-0" />
                  )}
                  <div>
                    <h4 className="text-xs font-semibold text-[var(--text-primary)]">
                      Sonido de alerta
                    </h4>
                    <p className="text-2xs text-[var(--text-muted)]">
                      Timbre suave al recibir mensajes
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className={`px-3 py-1 text-2xs font-semibold transition-colors ${
                    soundEnabled
                      ? "bg-accent text-white"
                      : "bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  }`}
                >
                  {soundEnabled ? "Activado" : "Silenciado"}
                </button>
              </div>

              {/* PWA App Card */}
              <div className="p-3.5 bg-[var(--bg-secondary)] flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Smartphone size={16} className="text-accent flex-shrink-0" />
                  <div>
                    <h4 className="text-xs font-semibold text-[var(--text-primary)]">
                      Instalación PWA
                    </h4>
                    <p className="text-2xs text-[var(--text-muted)]">
                      Acceso rápido como aplicación independiente
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleInstallApp}
                  className="px-3 py-1 text-2xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors flex-shrink-0"
                >
                  Instalar
                </button>
              </div>
            </div>
          )}

          {activeTab === "imap-smtp" && (
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {imapConfig.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-2.5 bg-[var(--bg-secondary)] text-xs"
                  >
                    <div className="min-w-0 pr-2">
                      <span className="text-[var(--text-muted)] font-bold block text-2xs uppercase tracking-wider">
                        {item.label}
                      </span>
                      <span className="font-mono text-xs text-[var(--text-primary)] font-semibold mt-0.5 block truncate">
                        {item.value}
                      </span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(item.value, item.id)}
                      className="p-1 hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors flex-shrink-0"
                      title="Copiar"
                    >
                      {copied === item.id ? (
                        <Check size={13} className="text-green-500" />
                      ) : (
                        <Copy size={13} />
                      )}
                    </button>
                  </div>
                ))}
              </div>

              {/* Endpoint de la API / Servidor Personalizado */}
              <div className="mt-4 p-3 bg-[var(--bg-secondary)] border border-[var(--border-subtle)] space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-semibold text-[var(--text-primary)]">
                      Endpoint de la API MagicMail
                    </h4>
                    <p className="text-2xs text-[var(--text-muted)]">
                      Servidor de conexión para la aplicación móvil y sincronización
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleResetServer}
                    className="text-2xs text-[var(--text-muted)] hover:text-accent underline flex items-center gap-1"
                  >
                    <RefreshCw size={11} />
                    Por defecto
                  </button>
                </div>
                <div className="flex gap-2 pt-1">
                  <input
                    type="text"
                    value={serverUrlInput}
                    onChange={(e) => setServerUrlInput(e.target.value)}
                    placeholder={DEFAULT_API_URL}
                    className="flex-1 px-3 py-1.5 text-xs font-mono border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] outline-none focus:border-accent"
                  />
                  <button
                    type="button"
                    onClick={handleSaveCustomServer}
                    className="px-3 py-1.5 text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors flex items-center gap-1 flex-shrink-0"
                  >
                    {serverSaved ? <Check size={13} /> : "Guardar"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "mcp" && (
            <div className="space-y-4">
              <div className="p-3 bg-[var(--bg-secondary)] border border-[var(--border-subtle)] text-xs space-y-2">
                <div className="flex items-center gap-2 text-accent font-semibold">
                  <Bot size={16} />
                  <span>Protocolo de Contexto de Modelo (MCP)</span>
                </div>
                <p className="text-[var(--text-secondary)] text-2xs leading-relaxed">
                  Conecta asistentes de Inteligencia Artificial (Claude Desktop, Cursor, Spark, agentes CLI) directamente a este buzón de correo.
                  Cada conexión está aislada criptográficamente a tu cuenta <strong>{user?.email}</strong> con remitente verificado y no puede acceder a otros buzones.
                </p>
              </div>

              {loadingMcp ? (
                <div className="flex items-center justify-center p-8 gap-2 text-xs text-[var(--text-muted)]">
                  <EmailSpinner size={16} />
                  <span>Generando credenciales MCP seguras...</span>
                </div>
              ) : mcpData ? (
                <div className="space-y-3">
                  {/* MCP URL directa con token */}
                  <div className="p-3 bg-[var(--bg-secondary)] space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--text-muted)] font-bold text-2xs uppercase tracking-wider">
                        URL de Conexión Directa (1 Clic)
                      </span>
                      <button
                        onClick={() => copyToClipboard(mcpData.mcpUrlWithToken || "", "mcp-url-token")}
                        className="p-1 hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-1 text-2xs"
                        title="Copiar URL completa"
                      >
                        {copied === "mcp-url-token" ? (
                          <>
                            <Check size={12} className="text-green-500" />
                            <span className="text-green-500">Copiado</span>
                          </>
                        ) : (
                          <>
                            <Copy size={12} />
                            <span>Copiar</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="font-mono text-2xs text-[var(--text-primary)] break-all bg-[var(--bg-primary)] p-2 border border-[var(--border-subtle)] select-all">
                      {mcpData.mcpUrlWithToken}
                    </div>
                    <span className="text-3xs text-[var(--text-muted)] block">
                      Úsala en clientes MCP compatibles con SSE / HTTP que acepten la URL completa.
                    </span>
                  </div>

                  {/* Token independiente */}
                  <div className="p-3 bg-[var(--bg-secondary)] space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--text-muted)] font-bold text-2xs uppercase tracking-wider">
                        Token de Autenticación Bearer
                      </span>
                      <button
                        onClick={() => copyToClipboard(mcpData.mcpToken || "", "mcp-token")}
                        className="p-1 hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-1 text-2xs"
                        title="Copiar Token"
                      >
                        {copied === "mcp-token" ? (
                          <>
                            <Check size={12} className="text-green-500" />
                            <span className="text-green-500">Copiado</span>
                          </>
                        ) : (
                          <>
                            <Copy size={12} />
                            <span>Copiar Token</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="font-mono text-2xs text-[var(--text-primary)] break-all bg-[var(--bg-primary)] p-2 border border-[var(--border-subtle)] select-all max-h-16 overflow-y-auto">
                      {mcpData.mcpToken}
                    </div>
                  </div>

                  {/* Configuración JSON para Claude Desktop / Cursor */}
                  <div className="p-3 bg-[var(--bg-secondary)] space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--text-muted)] font-bold text-2xs uppercase tracking-wider">
                        Configuración para Claude / Cursor (JSON)
                      </span>
                      <button
                        onClick={() => {
                          const configJson = JSON.stringify({
                            mcpServers: {
                              "magicmail": {
                                url: mcpData.mcpUrl,
                                headers: {
                                  Authorization: `Bearer ${mcpData.mcpToken}`
                                }
                              }
                            }
                          }, null, 2);
                          copyToClipboard(configJson, "mcp-json");
                        }}
                        className="p-1 hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-1 text-2xs"
                        title="Copiar JSON"
                      >
                        {copied === "mcp-json" ? (
                          <>
                            <Check size={12} className="text-green-500" />
                            <span className="text-green-500">Copiado</span>
                          </>
                        ) : (
                          <>
                            <Copy size={12} />
                            <span>Copiar JSON</span>
                          </>
                        )}
                      </button>
                    </div>
                    <pre className="font-mono text-3xs text-[var(--text-primary)] bg-[var(--bg-primary)] p-2 border border-[var(--border-subtle)] overflow-x-auto">
                      {JSON.stringify({
                        mcpServers: {
                          "magicmail": {
                            url: mcpData.mcpUrl,
                            headers: {
                              Authorization: `Bearer ${mcpData.mcpToken}`
                            }
                          }
                        }
                      }, null, 2)}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="text-center p-6 bg-[var(--bg-secondary)] space-y-2">
                  <p className="text-xs text-[var(--text-secondary)]">
                    No se ha generado el enlace MCP para este buzón.
                  </p>
                  <button
                    onClick={fetchMcpToken}
                    className="px-3 py-1.5 text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors inline-flex items-center gap-1.5"
                  >
                    <Key size={13} />
                    Generar Enlace y Token MCP
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {activeTab === "general" && (
          <div className="flex items-center justify-between px-5 py-2.5 border-t border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
            <span className="text-2xs text-[var(--text-muted)]">
              {saved && (
                <span className="text-green-500 font-semibold">Guardado ✓</span>
              )}
              {savedError && (
                <span className="text-red-500 font-semibold">{savedError}</span>
              )}
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-3 py-1 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-3.5 py-1 text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
              >
                {isSaving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}